/**
 * A person, not a row.
 *
 * The activity log showed the same generic icon on every entry with the name
 * demoted to a footnote, which made an audit trail out of what is actually a
 * record of people doing things. Initials read faster than a name at the start of
 * a line, and the colour makes one person's run of actions visible as a block.
 */

/**
 * Restrained tones, not a rainbow: the brand is red / white / charcoal and this
 * sits next to real content. Deterministic, so a person keeps their colour
 * everywhere and across reloads.
 */
const TONES = ["#3f3d3d", "#6b4a4a", "#44515c", "#5a5140", "#4a5a4e", "#5b4a5c"];

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  // First + last, so "Fahad Al-Ibrahim" is FA and not FI from the middle name.
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function toneFor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return TONES[hash % TONES.length];
}

export default function Avatar({ name, size = 30 }: { name: string | null; size?: number }) {
  // Not a person. Showing "SY" in a coloured circle would imply one.
  if (!name) {
    return (
      <span className="avatar avatar-system" style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }} title="Automatic">
        ⚙
      </span>
    );
  }
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, background: toneFor(name), fontSize: Math.round(size * 0.36) }}
      title={name}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}
